/*
 * @Author: Christion.Young
 * @Date: 2021-09-17 10:04:22
 * @LastEditors: Christion.Young
 * @LastEditTime: 2025-08-08 10:00:00
 * @Description: 路由管理 - 简化版
 */
import Vue from "vue";
import VueRouter from "vue-router";
import store from "./store";

Vue.use(VueRouter);

const routes = [
  // 首页
  {
    path: "/",
    name: "home",
    component: () => import("./views/Home/index.vue"),
    meta: { title: "首页" },
  },

  // 服务相关路由 - 纯展示
  {
    path: "/services",
    name: "services",
    component: () => import("./views/Services/List.vue"),
    meta: { title: "服务列表" },
  },
  {
    path: "/services/:id",
    name: "serviceDetail",
    component: () => import("./views/Services/Detail.vue"),
    meta: { title: "服务详情" },
  },

  // 商家相关路由 - 分为已登录和未登录
  {
    path: "/merchants",
    name: "merchants",
    component: () => import("./views/Merchant/NotLogList.vue"), // 未登录用户访问的商户列表
    meta: { title: "商家推荐" },
  },
  {
    path: "/merchants/logged",
    name: "merchantsLogged",
    component: () => import("./views/Merchant/AlreadyLoggedList.vue"), // 已登录用户访问的商户列表
    meta: {
      title: "商家推荐",
      requiresAuth: true, // 需要登录
    },
  },
  {
    path: "/merchants/:id",
    name: "merchantDetail",
    component: () => import("./views/Merchant/Detail.vue"),
    meta: { title: "店铺详情" },
  },

  // 用户中心 - 需要登录
  {
    path: "/user",
    name: "user",
    component: () => import("./views/User/Center.vue"),
    meta: {
      title: "用户中心",
      requiresAuth: true,
    },
  },

  // 聊天记录 - 需要登录
  {
    path: "/chat-history",
    name: "chatHistory",
    component: () => import("./views/ChatHistory/index.vue"),
    meta: {
      title: "聊天记录",
      requiresAuth: true,
    },
  },

  // 关于我们
  {
    path: "/about",
    name: "about",
    component: () => import("./views/About/index.vue"),
    meta: { title: "关于我们" },
  },

  // Demo保留
  {
    path: "/Demo/index",
    name: "Demo",
    component: () => import("./views/Demo/index.vue"),
    meta: { title: "Demo页面" },
  },

  // 兼容性重定向规则 - 处理常见的路径错误
  {
    path: "/service/:id",
    redirect: (to) => ({
      name: "serviceDetail",
      params: { id: to.params.id },
    }),
  },
  {
    path: "/merchant/:id",
    redirect: (to) => ({
      name: "merchantDetail",
      params: { id: to.params.id },
    }),
  },
  {
    path: "/service/detail",
    redirect: "/services",
  },
  {
    path: "/merchant/detail",
    redirect: "/merchants",
  },

  // 404页面 - 必须放在最后
  {
    path: "*",
    name: "404",
    component: () => import("./views/Error/404.vue"),
    meta: { title: "页面未找到" },
  },
];

// 处理重复跳转路由报错
const originalPush = VueRouter.prototype.push;
const originalReplace = VueRouter.prototype.replace;

VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    // 忽略重复导航错误
    if (err.name !== "NavigationDuplicated") {
      return Promise.reject(err);
    }
  });
};

VueRouter.prototype.replace = function replace(location) {
  return originalReplace.call(this, location).catch((err) => {
    // 忽略重复导航错误
    if (err.name !== "NavigationDuplicated") {
      return Promise.reject(err);
    }
  });
};

const router = new VueRouter({
  routes,
  mode: "history", // 使用HTML5 History模式
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },
});

/**
 * 检查路由是否需要认证
 * @param {Object} to - 目标路由
 * @returns {boolean} 是否需要认证
 */
function requiresAuth(to) {
  return to.matched.some((record) => record.meta.requiresAuth);
}

// 全局路由守卫
router.beforeEach(async (to, from, next) => {
  console.log("路由跳转:", from.path, "=>", to.path);

  // 设置页面标题
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} - 三火私服`;
  }

  // 验证路由参数
  if (to.params.id && !to.params.id.toString().trim()) {
    console.warn("路由参数 id 为空:", to);
    next("/404");
    return;
  }

  // 检查是否需要登录
  if (requiresAuth(to)) {
    const token = store.getters["user/token"];

    if (!token) {
      console.log("需要登录，显示登录弹窗");
      // 显示登录弹窗
      setTimeout(() => {
        if (router.app && router.app.$bus) {
          router.app.$bus.emit("show-login-dialog", {
            redirectPath: to.fullPath,
          });
        } else {
          console.warn("EventBus 未初始化，将跳转到首页");
          next("/");
          return;
        }
      }, 100);
      next(false); // 阻止路由跳转
      return;
    }

    // 更新登录状态
    store.commit("user/SET_LOGIN_STATUS", true);
    next();
  } else {
    // 不需要登录的页面直接通过
    next();
  }
});

// 路由后置守卫
router.afterEach((to) => {
  // 页面加载完成后的处理
  console.log("路由跳转完成:", to.path);
});

// 路由错误处理
router.onError((error) => {
  console.error("路由错误:", error);

  // 如果是加载组件失败，跳转到404页面
  if (
    error.message.includes("Loading chunk") ||
    error.message.includes("Loading CSS chunk") ||
    error.message.includes("ChunkLoadError")
  ) {
    console.log("组件加载失败，跳转到404页面");
    router.push("/404");
  }
});

export default router;
